import React, { useEffect, useState } from "react";
import { Card, Col, Modal, Row } from "react-bootstrap";
import { rockRealOrFakeApi } from "../../services/rockRealOrFakeApi";
import ErrorAlert from "../common/ErrorAlert";
import LoadingBlock from "../common/LoadingBlock";

function ImageGrid({ images }) {
  const items = (images || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <Row className="g-2 mt-1">
      {items.map((src, i) => (
        <Col key={i} xs={6} sm={4}>
          <img
            alt=""
            src={src}
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              objectFit: "cover",
              borderRadius: 6,
              border: "1px solid var(--tblr-border-color)"
            }}
          />
        </Col>
      ))}
    </Row>
  );
}

function CommonField({ label, field }) {
  if (!field?.text) return null;
  return (
    <div className="mb-3">
      <div className="fw-semibold small text-muted text-uppercase mb-1">{label}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{field.text}</div>
      <ImageGrid images={field.images} />
    </div>
  );
}

function SectionCard({ section }) {
  return (
    <Card className="mb-3">
      <Card.Header className="py-2">
        <Card.Title className="mb-0 fs-6">{section.title}</Card.Title>
      </Card.Header>
      <Card.Body className="p-0">
        <Row className="g-0">
          <Col xs={12} md={6} className="p-3" style={{ borderRight: "1px solid var(--tblr-border-color)" }}>
            <div
              className="d-flex align-items-center gap-1 mb-2 fw-semibold small text-uppercase"
              style={{ color: "var(--tblr-success)" }}
            >
              <span
                className="badge"
                style={{ background: "var(--tblr-success)", fontSize: "0.65rem", color: "white" }}
              >
                REAL
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{section.real?.text || "—"}</div>
            <ImageGrid images={section.real?.images} />
          </Col>
          <Col xs={12} md={6} className="p-3">
            <div
              className="d-flex align-items-center gap-1 mb-2 fw-semibold small text-uppercase"
              style={{ color: "var(--tblr-danger)" }}
            >
              <span
                className="badge"
                style={{ background: "var(--tblr-danger)", fontSize: "0.65rem", color: "white" }}
              >
                FAKE
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{section.fake?.text || "—"}</div>
            <ImageGrid images={section.fake?.images} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function RealOrFakeDetailView({ uid }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [item, setItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!uid) return;
      setLoading(true);
      setError(null);
      setItem(null);
      try {
        const data = await rockRealOrFakeApi.getDetail(uid);
        if (!cancelled) setItem(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [uid]);

  if (loading) return <LoadingBlock label="Loading..." />;
  if (error) return <ErrorAlert error={error} title="Failed to load detail" />;
  if (!item) return null;

  const common = item.attributes?.common ?? {};
  const sections = item.attributes?.sections ?? [];

  return (
    <div>
      {/* Header */}
      <div className="d-flex gap-3 mb-4 align-items-start">
        {item.main_image_url && (
          <img
            alt={item.common_name}
            src={item.main_image_url}
            style={{
              width: 100,
              height: 100,
              objectFit: "cover",
              borderRadius: 10,
              border: "1px solid var(--tblr-border-color)",
              flexShrink: 0
            }}
          />
        )}
        <div>
          <h4 className="mb-0">{item.common_name}</h4>
          {item.latin_name && item.latin_name !== item.common_name && (
            <div className="text-muted fst-italic">{item.latin_name}</div>
          )}
          {item.updated_at && (
            <div className="text-muted small mt-1">
              Updated: {new Date(item.updated_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Common info */}
      {(common.description?.text || common.summary?.text || common.notice?.text) && (
        <Card className="mb-4">
          <Card.Header className="py-2">
            <Card.Title className="mb-0 fs-6">Overview</Card.Title>
          </Card.Header>
          <Card.Body>
            <CommonField label="Description" field={common.description} />
            <CommonField label="Summary" field={common.summary} />
            {common.notice?.text && (
              <div
                className="alert alert-warning mb-0 small"
                style={{ whiteSpace: "pre-wrap" }}
              >
                <strong>Note: </strong>{common.notice.text}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Comparison sections */}
      {sections.length > 0 && (
        <>
          <div className="fw-semibold text-muted small text-uppercase mb-2">
            Real vs Fake — {sections.length} test{sections.length !== 1 ? "s" : ""}
          </div>
          {sections.map((s) => (
            <SectionCard key={s.key} section={s} />
          ))}
        </>
      )}
    </div>
  );
}

export default function RealOrFakeDetailModal({ show, onHide, uid, title }) {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title || "Real or Fake"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {uid ? <RealOrFakeDetailView uid={uid} /> : null}
      </Modal.Body>
    </Modal>
  );
}
